import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import {
  getExamDetail,
  getExamResult,
  getExamScaledScore,
  getExamStudentResult,
} from '@apis/exam';
import { useIsTeacher } from '@reducers/user/hooks';
import { getSectionEstimatedScores } from '@apis/sections';
import { useCurrentSection } from '@reducers/sections/hooks';
import { ExamDetailsObject, IExamResults, ScaledScoreData } from '../ExamScoreCard/types';

// We have a constraint in the backend with really old legacy code matching by node name.
// Because of this it isnt that easy to update ACT -> Preparing for Higher Math to just 'Math'
// To avoid client confusion we'll rename the node to 'Math' in the response from the backend.
// Trust me, this is the saner option.
const ACT_MATH_SLUG = 'preparing-for-higher-math';
const ACT_MATH_NAME = 'Preparing for Higher Math';
const renameACTMath = <T, K extends keyof T>(data: T, key: K): T => {
  const isACTMathNode = (node: { slug?: string; name?: string }) => node?.slug === ACT_MATH_SLUG || node?.name === ACT_MATH_NAME;
  const nodes = data?.[key];
  if (!Array.isArray(nodes)) {
    return data;
  }
  return {
    ...data,
    [key]: nodes.map((node) => (isACTMathNode(node) ? { ...node, name: 'Math' } : node)),
  };
};

export const useGetExamScaledScore = () => {
  const { examSessionId, testSessionId } = useParams();

  const queryResponse = useQuery<ScaledScoreData>(
    ['getExamScaledScore', examSessionId || testSessionId],
    async () => {
      const { response } = (await getExamScaledScore(examSessionId || testSessionId)) as unknown as {
        response: { data: ScaledScoreData };
      };

      return renameACTMath(response.data, 'subscores') || [];
    },
    {
      suspense: true,
      useErrorBoundary: true,
      enabled: !!examSessionId || !!testSessionId,
      refetchOnMount: 'always',
      staleTime: 1000 * 60 * 15,
    },
  );

  return queryResponse;
};

export const useGetExamDetail = () => {
  const { examSessionId, testSessionId } = useParams();
  const section = useCurrentSection();
  const activeExamSessionId = examSessionId || testSessionId || section.exams?.[0];
  if (!activeExamSessionId && section && process.env.NODE_ENV === 'development') {
    console.warn('examSessionId is not defined in useGetExamDetail');
  }

  const queryResponse = useQuery<ExamDetailsObject>(
    ['getExamDetails', activeExamSessionId],
    async () => {
      const { response } = (await getExamDetail(activeExamSessionId)) as unknown as {
        response: { data: ExamDetailsObject };
      };

      return response.data || {};
    },
    {
      suspense: true,
      enabled: !!activeExamSessionId,
      refetchOnMount: 'always',
      staleTime: 1000 * 60 * 15,
    },
  );

  return queryResponse;
};

export const useGetExamResult = () => {
  const { examSessionId, testSessionId } = useParams();
  const isTeacher = useIsTeacher();

  const queryResponse = useQuery<Array<IExamResults>>(
    ['getExamResult', examSessionId || testSessionId],
    async () => {
      const { response } = (await getExamResult(
        examSessionId || testSessionId,
      )) as unknown as {
        response: { data: Array<IExamResults> };
      };

      return response.data || {};
    },
    {
      useErrorBoundary: true,
      suspense: true,
      enabled: !!(examSessionId || testSessionId) && isTeacher,
      refetchOnMount: 'always',
      staleTime: 1000 * 60 * 15,
    },
  );

  return queryResponse;
};
export const useGetExamStudentResult = () => {
  const { examSessionId, testSessionId } = useParams();
  const isTeacher = useIsTeacher();

  const queryResponse = useQuery<IExamResults>(
    ['getExamStudentResult', examSessionId || testSessionId],
    async () => {
      const { response } = (await getExamStudentResult(
        examSessionId || testSessionId,
      )) as unknown as {
        response: { data: IExamResults };
      };

      return response.data || {};
    },
    {
      useErrorBoundary: true,
      suspense: true,
      enabled: !!(examSessionId || testSessionId) && !isTeacher,
      refetchOnMount: 'always',
      staleTime: 1000 * 60 * 15,
    },
  );

  return queryResponse;
};

export const useGetExamEstimatedScore = () => {
  const { sectionId } = useParams();

  const queryResponse = useQuery<any>(
    ['getSectionEstimatedScores', sectionId],
    async () => {
      try {
        const { response } = (await getSectionEstimatedScores(sectionId)) as unknown as {
          response: { data: any };
        };

        return renameACTMath(response.data, 'subjects') || [];
      } catch (error) {
        return [];
      }
    },
    {
      suspense: true,
      enabled: !!sectionId,
      staleTime: 1000 * 60 * 15,
    },
  );

  return queryResponse;
};
